/* iter-training-v-ach-coach — Training Center admin panel.
 *
 * Lets an operator paste optional Loom/YouTube/direct-mp4 URLs per
 * training lesson. Empty → no player is shown for that lesson.
 * Persists via PUT /api/admin/training-videos (single doc in
 * `app_config.training_videos`).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GraduationCap, Save, Loader2, PlayCircle, Link as LinkIcon } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LESSONS = [
  { id: 'train-l1-orient',    title: 'L1 · Orient — workspace panels' },
  { id: 'train-l2-seed',      title: 'L2 · Seed the story' },
  { id: 'train-l3-framework', title: 'L3 · Structure your novel' },
  { id: 'train-l4-bibles',    title: 'L4 · Fill the Bibles' },
  { id: 'train-l5-audit',     title: 'L5 · Preview the prompt' },
  { id: 'train-l6-draft',     title: 'L6 · Draft chapter one' },
  { id: 'train-l7-polish',    title: 'L7 · Polish' },
  { id: 'train-l8-ship',      title: 'L8 · Ship it' },
];

function looksValid(url) {
  if (!url) return true;                    // empty is valid (clears the field)
  try { const u = new URL(url); return u.protocol === 'http:' || u.protocol === 'https:'; }
  catch { return false; }
}

export default function TrainingAdminPanel() {
  const [videos, setVideos] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/training-videos`);
      setVideos(r.data?.videos || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load videos.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    // Validate before firing.
    for (const l of LESSONS) {
      if (!looksValid(videos[l.id] || '')) {
        toast.error(`Invalid URL for ${l.title}. Use http(s):// or leave blank.`);
        return;
      }
    }
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(
        LESSONS.map((l) => [l.id, (videos[l.id] || '').trim()])
               .filter(([, v]) => v),
      );
      const r = await axios.put(`${API}/admin/training-videos`, { videos: cleaned });
      setVideos(r.data?.videos || {});
      toast.success('Training videos saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4" data-testid="admin-training-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-[18px] font-semibold"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            <GraduationCap className="h-5 w-5 text-primary" />
            Training Center · Video Micro-Explainers
          </h2>
          <p className="mt-1 text-[12px] text-muted-foreground">
            Paste a Loom, YouTube, or direct-video URL per lesson. Writers see a
            <span className="mx-1 inline-flex items-center gap-0.5 text-primary">
              <PlayCircle className="h-3 w-3" /> Watch
            </span>
            button in the Training Center that opens an overlay player. Leave a
            field blank to hide the button for that lesson.
          </p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="admin-training-save-btn"
                className="shrink-0 gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-3">
          {LESSONS.map((l) => {
            const val = videos[l.id] || '';
            const bad = !looksValid(val);
            return (
              <div key={l.id}
                   className="rounded-md border border-border bg-card p-3 shadow-sm"
                   data-testid={`admin-training-row-${l.id}`}>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {l.title}
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <Input
                    type="url"
                    value={val}
                    onChange={(e) => setVideos((v) => ({ ...v, [l.id]: e.target.value }))}
                    placeholder="https://www.loom.com/share/… or https://youtu.be/…"
                    className={bad ? 'border-destructive' : ''}
                    data-testid={`admin-training-input-${l.id}`}
                  />
                </div>
                {bad && (
                  <div className="mt-1 text-[10.5px] text-destructive">
                    Must be an http:// or https:// URL.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
